"use client";

import React, { useState } from "react";
import { 
  Utensils, 
  Wine, 
  Calendar, 
  Clock, 
  Users, 
  ArrowRight, 
  CheckCircle2, 
  Sparkles,
  Receipt,
  AlertCircle
} from "lucide-react";
import { RestaurantReservation } from "@/lib/demos/restaurantDb";

interface RestaurantKanbanProps {
  reservations: RestaurantReservation[];
  onSelectReservation: (r: RestaurantReservation) => void;
  onUpdateStage: (reservationId: number, newStage: RestaurantReservation["stage"]) => void;
  currency?: "INR" | "USD";
}

interface StageColumnConfig {
  id: RestaurantReservation["stage"];
  title: string;
  dotColor: string;
  badgeBg: string;
  description: string;
}

const RESTAURANT_STAGES: StageColumnConfig[] = [
  {
    id: "New Requests",
    title: "New Requests",
    dotColor: "bg-blue-400",
    badgeBg: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    description: "Web bookings & concierge inquiries",
  },
  {
    id: "Table Reserved",
    title: "Table Reserved",
    dotColor: "bg-amber-400",
    badgeBg: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    description: "Table assigned & voucher emailed",
  },
  {
    id: "Menu Curated",
    title: "Menu Curated",
    dotColor: "bg-indigo-400",
    badgeBg: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20",
    description: "Sommelier pairings & dietary locked",
  },
  {
    id: "Seated & Dining",
    title: "Seated & Dining",
    dotColor: "bg-orange-400",
    badgeBg: "bg-orange-500/10 text-orange-400 border-orange-500/20",
    description: "Guest seated, active table service",
  },
  {
    id: "Billed & Paid",
    title: "Billed & Paid (Won)",
    dotColor: "bg-emerald-400",
    badgeBg: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    description: "Bill settled & itemized tax receipt sent",
  },
  {
    id: "Cancelled",
    title: "Cancelled / No-Show",
    dotColor: "bg-stone-500",
    badgeBg: "bg-stone-500/10 text-stone-400 border-stone-500/20",
    description: "Schedule conflict or cancellation",
  },
];

export default function RestaurantKanban({
  reservations,
  onSelectReservation,
  onUpdateStage,
  currency = "INR",
}: RestaurantKanbanProps) {
  const [draggedId, setDraggedId] = useState<number | null>(null);

  const handleDragStart = (e: React.DragEvent, id: number) => {
    e.dataTransfer.setData("text/plain", id.toString());
    setDraggedId(id);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, targetStage: RestaurantReservation["stage"]) => {
    e.preventDefault();
    const idStr = e.dataTransfer.getData("text/plain");
    const id = parseInt(idStr, 10);
    if (!isNaN(id)) {
      onUpdateStage(id, targetStage);
    }
    setDraggedId(null);
  };

  const sym = currency === "USD" ? "$" : "₹";

  return (
    <div className="flex gap-4 overflow-x-auto pb-6 pt-2 select-none scrollbar-thin scrollbar-thumb-stone-800">
      {RESTAURANT_STAGES.map((col) => {
        const colReservations = reservations.filter((r) => r.stage === col.id);
        const colRevenue = colReservations.reduce((sum, r) => sum + (r.estimated_spend || 0), 0);
        const colCovers = colReservations.reduce((sum, r) => sum + (r.party_size || 0), 0);

        return (
          <div
            key={col.id}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, col.id)}
            className="w-80 shrink-0 bg-stone-950/80 rounded-2xl border border-stone-800/90 flex flex-col max-h-[calc(100vh-230px)] shadow-xl"
          >
            {/* Column Header */}
            <div className="p-3.5 border-b border-stone-800/80 bg-stone-900/60 rounded-t-2xl space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${col.dotColor}`} />
                  <h3 className="text-xs font-bold text-white tracking-tight">{col.title}</h3>
                </div>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-stone-800 text-stone-300">
                  {colReservations.length}
                </span>
              </div>

              <div className="flex items-center justify-between text-[11px] text-stone-400">
                <span>{colCovers} Total Covers</span>
                <span className="font-semibold text-amber-400">{sym}{colRevenue.toLocaleString()}</span>
              </div>
            </div>

            {/* Cards Container */}
            <div className="p-3 overflow-y-auto space-y-3 flex-1 scrollbar-thin scrollbar-thumb-stone-800">
              {colReservations.length === 0 ? (
                <div className="h-28 border-2 border-dashed border-stone-800/70 rounded-xl flex items-center justify-center text-xs text-stone-400">
                  Drop reservations here
                </div>
              ) : (
                colReservations.map((res) => (
                  <div
                    key={res.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, res.id)}
                    onClick={() => onSelectReservation(res)}
                    className="p-4 rounded-xl bg-stone-900/90 border border-stone-800 hover:border-amber-500/40 cursor-grab active:cursor-grabbing hover:shadow-lg transition-all duration-200 space-y-3 group"
                  >
                    {/* Top Row: Guest Name & Table */}
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="text-xs font-bold text-white group-hover:text-amber-300 transition-colors">
                          {res.guest_name}
                        </h4>
                        <span className="text-[11px] text-stone-400 block truncate max-w-[170px]">
                          {res.email}
                        </span>
                      </div>

                      {res.table_number && (
                        <span className="text-[10px] font-bold text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20 shrink-0">
                          {res.table_number}
                        </span>
                      )}
                    </div>

                    {/* Sitting Itinerary */}
                    <div className="p-2 bg-stone-950/80 rounded-lg border border-stone-800/80 text-[11px] text-stone-300 space-y-1">
                      <div className="flex items-center justify-between text-stone-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-stone-500" />
                          {res.reservation_date}
                        </span>
                        <span className="flex items-center gap-1 font-semibold text-white">
                          <Users className="w-3 h-3 text-amber-400" />
                          {res.party_size} Guests
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-amber-300/90 truncate">
                        <Clock className="w-3 h-3 text-amber-500 shrink-0" />
                        <span className="truncate">{res.time_slot}</span>
                      </div>
                    </div>

                    {/* Dining Salon & Menu */}
                    <div className="space-y-1 text-[11px]">
                      <div className="flex items-center justify-between">
                        <span className="text-stone-400 truncate max-w-[170px]">{res.dining_area}</span>
                        <span className="font-bold text-emerald-400 shrink-0">
                          {sym}{res.estimated_spend.toLocaleString()}
                        </span>
                      </div>
                      <div className="text-[10px] text-stone-400 truncate">
                        {res.tasting_menu}
                      </div>
                    </div>

                    {/* Dietary / Special tag */}
                    {res.dietary_notes && (
                      <div className="text-[10px] text-amber-400 bg-amber-500/10 px-2 py-1 rounded truncate border border-amber-500/20">
                        Dietary: {res.dietary_notes}
                      </div>
                    )}

                    {/* Footer: Sommelier */}
                    <div className="pt-2 border-t border-stone-800/80 flex items-center justify-between text-[10px] text-stone-400">
                      <span className="truncate max-w-[180px]">Host: {res.assigned_sommelier}</span>
                      <span className="text-stone-400 group-hover:text-amber-400 transition-colors">Details →</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
